import { IonText, useIonToast } from '@ionic/react';
import { Business } from 'model';
import React from 'react';
import { useHistory } from 'react-router';
import { useUpdateBusinessMutation } from '../api/BusinessApi';
import { IconButton } from '../components/IconButton';
import { PreviewUser } from '../components/PreviewUser';
import { IconCheck, IconCheckCircle, IconDeactivate, IconUpload } from '../components/QuirkIcons';
import { useUserHasRole } from '../hooks/useUserHasRole';
import { isMutationError } from '../util/error';
import styles from './BusinessApprovals.module.css';

export interface BusinessApprovalProps {
	business: Business;
}

export const BusinessApprovals: React.FC<BusinessApprovalProps> = ({ business }: BusinessApprovalProps) => {
	const isAdmin = useUserHasRole('admin');
	const history = useHistory();
	const isPending = business.submissionStatus === 'pending' && business.submittedBy !== '';
	const isClaimed = business.claimStatus === 'pending' && business.owner !== '';
	const [updateBusiness, { isLoading: isUpdating }] = useUpdateBusinessMutation();
	const [showToast] = useIonToast();
	const handleApproveSubmissionClick = async () => {
		if (isPending) {
			history.push(`/main/bus/approve/${business._id}`);
		} else {
			const result = await updateBusiness({
				business: {
					...business,
					claimStatus: isClaimed ? 'claimed' : business.claimStatus
				}
			});
			if (isMutationError(result)) {
				showToast({
					message: 'Error updating Business',
					duration: 1500,
					color: 'warning',
					position: 'top'
				});
			} else {
				showToast({
					message: 'Business claim approved',
					duration: 1500,
					color: 'success',
					position: 'top'
				});
			}
		}
	};
	const handleRejectSubmissionClick = async () => {
		const result = await updateBusiness({
			business: {
				...business,
				claimStatus: isClaimed ? 'unclaimed' : business.claimStatus,
				owner: isClaimed ? '' : business.owner,
				submissionStatus: isPending ? 'rejected' : business.submissionStatus
			}
		});
		if (isMutationError(result)) {
			showToast({
				message: 'Error updating Business',
				duration: 1500,
				color: 'warning',
				position: 'top'
			});
		} else {
			showToast({
				message: 'Business submission rejected',
				duration: 1500,
				color: 'success',
				position: 'top'
			});
		}
	};

	if (!(isPending || isClaimed) || !isAdmin) {
		return null;
	}
	return (
		<div className={styles.BusinessApprovals}>
			<div className={styles.actionsWrapper}>
				<IconButton
					label="Approve"
					icon={<IconCheck />}
					disabled={isUpdating}
					onClick={handleApproveSubmissionClick}
					className={styles.approve}
				/>
				<IconButton
					label="Reject"
					icon={<IconDeactivate />}
					disabled={isUpdating}
					onClick={handleRejectSubmissionClick}
					className={styles.reject}
				/>
			</div>
			<div className={styles.approvalTypes}>
				{isPending && (
					<IonText className={styles.claimedBanner}>
						<IconUpload size="large" />
						User suggested business
					</IonText>
				)}
				{isClaimed && (
					<IonText className={styles.claimedBanner}>
						<IconCheckCircle size="large" />
						User claiming ownership
					</IonText>
				)}
			</div>
			<PreviewUser userId={isPending ? business.submittedBy : business.owner} />
		</div>
	);
};
